import "webrtc-adapter";
import SimplePeerModule from "simple-peer";
import { getStoredJwt } from "./authApi";

/**
 * Vite bundles simple-peer as CJS; default export is sometimes `{ default: Peer }`.
 * Wrong constructor → immediate throw and "Не удалось начать звонок".
 */
const SimplePeerCtor: typeof SimplePeerModule =
  typeof SimplePeerModule === "function"
    ? SimplePeerModule
    : (SimplePeerModule as unknown as { default: typeof SimplePeerModule }).default;

type SimplePeerInstance = InstanceType<typeof SimplePeerCtor>;
type SimplePeerSignalArg = Parameters<SimplePeerInstance["signal"]>[0];
type SimplePeerWrtc = NonNullable<
  NonNullable<ConstructorParameters<typeof SimplePeerCtor>[0]>["wrtc"]
>;

/**
 * Same discovery as get-browser-rtc, but evaluated in app scope (avoids bundler/globalThis quirks).
 * Only RTCPeerConnection is required to construct the PC; SDP/candidate constructors may be prefixed.
 */
function explicitBrowserWrtc(): SimplePeerWrtc | undefined {
  const g = globalThis as typeof globalThis & Record<string, unknown>;
  const RTCPC =
    g.RTCPeerConnection || g.webkitRTCPeerConnection || g.mozRTCPeerConnection;
  if (typeof RTCPC !== "function") return undefined;
  return {
    RTCPeerConnection: RTCPC,
    RTCSessionDescription:
      g.RTCSessionDescription || g.mozRTCSessionDescription || g.webkitRTCSessionDescription,
    RTCIceCandidate: g.RTCIceCandidate || g.mozRTCIceCandidate || g.webkitRTCIceCandidate,
  } as SimplePeerWrtc;
}

export type VoiceCallUi =
  | { phase: "idle" }
  | { phase: "outgoing"; peerId: string; name: string }
  | { phase: "incoming"; peerId: string; name: string; callId: string }
  | { phase: "connected"; peerId: string; name: string; muted: boolean }
  | { phase: "missed"; message: string }
  | { phase: "ended"; message: string };

const DEFAULT_STUN: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

/** Parity with previous RTCPeerConnection setup (simple-peer passes `config` through). */
function buildVoiceRtcConfig(iceServers: RTCIceServer[]): RTCConfiguration {
  return {
    iceServers,
    bundlePolicy: "max-bundle",
    rtcpMuxPolicy: "require",
  };
}

/**
 * Build-time override (`VITE_STYX_ICE_JSON`) wins; otherwise `GET /api/ice` (JWT) when logged in;
 * fallback to public STUN only.
 */
async function resolveIceServers(): Promise<RTCIceServer[]> {
  const raw = import.meta.env.VITE_STYX_ICE_JSON as string | undefined;
  if (raw && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed as RTCIceServer[];
    } catch {
      /* fall through */
    }
  }
  const token = getStoredJwt();
  if (!token) return DEFAULT_STUN;
  try {
    const res = await fetch("/api/ice", { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("ice http");
    const data = (await res.json()) as { ice_servers?: unknown };
    if (Array.isArray(data.ice_servers) && data.ice_servers.length > 0) {
      return data.ice_servers as RTCIceServer[];
    }
  } catch {
    /* keep default */
  }
  return DEFAULT_STUN;
}

/** Mono voice, wideband where supported; processing flags are explicit (defaults vary by browser). */
const VOICE_AUDIO: MediaTrackConstraints = {
  channelCount: { ideal: 1 },
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: { ideal: 48000 },
};

async function getVoiceMicStream(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: VOICE_AUDIO, video: false });
  } catch {
    return await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  }
}

/**
 * Raise Opus voice bitrate above browser defaults (often ~32 kb/s) for clearer speech.
 * Safe no-op if the runtime rejects parameter tweaks.
 */
async function tuneVoiceSenders(pc: RTCPeerConnection): Promise<void> {
  const maxBitrate = 128_000;
  for (const sender of pc.getSenders()) {
    if (sender.track?.kind !== "audio") continue;
    try {
      const params = sender.getParameters();
      if (!params.encodings.length) params.encodings.push({});
      const enc = params.encodings[0];
      if (enc) {
        enc.maxBitrate = maxBitrate;
        enc.networkPriority = "high";
      }
      await sender.setParameters(params);
    } catch {
      /* keep defaults */
    }
  }
}

function peerUnderlyingPc(peer: SimplePeerInstance): RTCPeerConnection | null {
  const pc = (peer as unknown as { _pc?: RTCPeerConnection | null })._pc;
  return pc ?? null;
}

function isOfferSignal(signal: Record<string, unknown>): boolean {
  return signal.type === "offer" && typeof signal.sdp === "string";
}

export class VoiceCallSession {
  private peer: SimplePeerInstance | null = null;
  private localStream: MediaStream | null = null;
  private callId: string | null = null;
  private peerId: string | null = null;
  private role: "caller" | "callee" | null = null;
  /** Callee: simple-peer signals (offer + trickle) until user accepts. */
  private pendingIncoming: {
    callId: string;
    fromId: string;
    buffered: Record<string, unknown>[];
  } | null = null;
  private remoteAudioEl: HTMLAudioElement | null = null;
  /** Avoid duplicate `call.hangup` when user hangs up and peer emits error/close. */
  private hangupSent = false;
  /** True while we tear down after local user action or remote ended (no failure hangup). */
  private quietClose = false;

  constructor(
    private readonly sendSignal: (peerId: string, callId: string, signal: Record<string, unknown>) => void,
    private readonly sendHangup: (peerId: string, callId: string) => void,
    private readonly setUi: (u: VoiceCallUi) => void,
    private readonly resolveName: (userId: string) => string | undefined,
  ) {}

  setRemoteAudioElement(el: HTMLAudioElement | null): void {
    this.remoteAudioEl = el;
  }

  private resetSessionIds(): void {
    this.callId = null;
    this.peerId = null;
    this.role = null;
    this.hangupSent = false;
    this.quietClose = false;
  }

  private destroyPeerInstance(): void {
    const p = this.peer;
    if (!p) return;
    this.peer = null;
    try {
      p.destroy();
    } catch {
      /* ignore */
    }
  }

  private cleanupMedia(): void {
    this.quietClose = true;
    this.destroyPeerInstance();
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    this.pendingIncoming = null;
    this.resetSessionIds();
  }

  destroy(): void {
    this.cleanupMedia();
    this.setUi({ phase: "idle" });
  }

  private sendHangupOnce(): void {
    const pid = this.peerId;
    const cid = this.callId;
    if (!pid || !cid || this.hangupSent) return;
    this.hangupSent = true;
    this.sendHangup(pid, cid);
  }

  /** Read `remoteAudioEl` when the event fires so we never attach to a stale DOM node. */
  private applyRemoteStream(stream: MediaStream): void {
    const audioEl = this.remoteAudioEl;
    if (!audioEl || !stream) return;
    audioEl.srcObject = stream;
    const tryPlay = () => {
      void audioEl.play().catch(() => {
        setTimeout(tryPlay, 200);
      });
    };
    tryPlay();
  }

  private failConnection(message: string): void {
    if (this.quietClose) return;
    this.quietClose = true;
    this.sendHangupOnce();
    this.destroyPeerInstance();
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    this.pendingIncoming = null;
    this.resetSessionIds();
    this.setUi({ phase: "ended", message });
  }

  private wirePeer(peer: SimplePeerInstance, remotePeerId: string, activeCallId: string): void {
    peer.on("signal", (data: SimplePeerSignalArg) => {
      if (this.peer !== peer) return;
      this.sendSignal(remotePeerId, activeCallId, data as Record<string, unknown>);
    });

    peer.on("stream", (stream: MediaStream) => {
      if (this.peer !== peer) return;
      this.applyRemoteStream(stream);
      const name = this.resolveName(remotePeerId) ?? remotePeerId;
      this.setUi({ phase: "connected", peerId: remotePeerId, name, muted: false });
    });

    peer.on("connect", () => {
      if (this.peer !== peer) return;
      const pc = peerUnderlyingPc(peer);
      if (pc) void tuneVoiceSenders(pc);
    });

    peer.on("error", () => {
      if (this.peer !== peer || this.quietClose) return;
      this.failConnection("Не удалось установить соединение (сеть / NAT)");
    });

    peer.on("close", () => {
      if (this.peer !== peer) return;
      this.peer = null;
      if (this.quietClose) return;
      this.failConnection("Не удалось установить соединение (сеть / NAT)");
    });
  }

  async startOutgoing(peerId: string): Promise<void> {
    if (this.peer || this.pendingIncoming) {
      this.setUi({ phase: "ended", message: "Уже есть активный звонок" });
      return;
    }
    const callId = crypto.randomUUID();
    this.callId = callId;
    this.peerId = peerId;
    this.role = "caller";
    this.hangupSent = false;
    this.quietClose = false;
    const name = this.resolveName(peerId) ?? peerId;
    this.setUi({ phase: "outgoing", peerId, name });

    const iceServers = await resolveIceServers();

    let stream: MediaStream;
    try {
      stream = await getVoiceMicStream();
    } catch {
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Нет доступа к микрофону" });
      return;
    }
    this.localStream = stream;

    if (typeof SimplePeerCtor !== "function") {
      console.error("[VoiceCall] simple-peer export is not a constructor:", SimplePeerModule);
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Не удалось начать звонок" });
      return;
    }

    const wrtc = explicitBrowserWrtc();
    if (!wrtc) {
      console.warn("[VoiceCall] WebRTC API missing on globalThis (need HTTPS or a supported browser)");
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Не удалось начать звонок" });
      return;
    }

    try {
      const peer = new SimplePeerCtor({
        initiator: true,
        trickle: true,
        stream,
        wrtc,
        config: buildVoiceRtcConfig(iceServers),
      });
      this.peer = peer;
      this.wirePeer(peer, peerId, callId);
    } catch (e) {
      console.warn("[VoiceCall] SimplePeer constructor failed:", e);
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Не удалось начать звонок" });
    }
  }

  async acceptIncoming(): Promise<void> {
    const pending = this.pendingIncoming;
    if (!pending) return;

    const { fromId, callId, buffered } = pending;
    this.pendingIncoming = null;

    const iceServers = await resolveIceServers();

    let stream: MediaStream;
    try {
      stream = await getVoiceMicStream();
    } catch {
      this.sendHangup(fromId, callId);
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Нет доступа к микрофону" });
      return;
    }
    this.localStream = stream;

    this.hangupSent = false;
    this.quietClose = false;

    const wrtcCallee = explicitBrowserWrtc();
    if (!wrtcCallee) {
      this.sendHangup(fromId, callId);
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Не удалось ответить" });
      return;
    }

    try {
      const peer = new SimplePeerCtor({
        initiator: false,
        trickle: true,
        stream,
        wrtc: wrtcCallee,
        config: buildVoiceRtcConfig(iceServers),
      });
      this.peer = peer;
      this.wirePeer(peer, fromId, callId);

      for (const data of buffered) {
        try {
          peer.signal(data as SimplePeerSignalArg);
        } catch {
          this.sendHangup(fromId, callId);
          this.cleanupMedia();
          this.setUi({ phase: "ended", message: "Не удалось ответить" });
          return;
        }
      }
      const calleeName = this.resolveName(fromId) ?? fromId;
      this.setUi({ phase: "connected", peerId: fromId, name: calleeName, muted: false });
    } catch {
      this.sendHangup(fromId, callId);
      this.cleanupMedia();
      this.setUi({ phase: "ended", message: "Не удалось ответить" });
    }
  }

  rejectIncoming(): void {
    const pending = this.pendingIncoming;
    if (!pending) return;
    this.sendHangup(pending.fromId, pending.callId);
    this.cleanupMedia();
    this.setUi({ phase: "idle" });
  }

  hangupLocal(): void {
    this.quietClose = true;
    this.sendHangupOnce();
    this.destroyPeerInstance();
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    this.pendingIncoming = null;
    this.resetSessionIds();
    this.setUi({ phase: "idle" });
  }

  toggleMicMute(): void {
    const tracks = this.localStream?.getAudioTracks() ?? [];
    if (!tracks.length) return;
    const track = tracks[0]!;
    track.enabled = !track.enabled;
    const muted = !track.enabled;
    const pid = this.peerId ?? "";
    const name = pid ? (this.resolveName(pid) ?? pid) : "";
    this.setUi({ phase: "connected", peerId: pid, name, muted });
  }

  handleRelay(fromId: string, callId: string, signal: Record<string, unknown>): void {
    if (this.peer) {
      if (callId !== this.callId || fromId !== this.peerId) return;
      try {
        this.peer.signal(signal as SimplePeerSignalArg);
        if (this.role === "caller" && signal.type === "answer") {
          const name = this.resolveName(fromId) ?? fromId;
          this.setUi({ phase: "connected", peerId: fromId, name, muted: false });
        }
      } catch {
        this.failConnection("Ошибка соединения");
      }
      return;
    }

    if (this.pendingIncoming) {
      if (callId !== this.pendingIncoming.callId || fromId !== this.pendingIncoming.fromId) return;
      this.pendingIncoming.buffered.push(signal);
      return;
    }

    if (!isOfferSignal(signal)) return;

    this.callId = callId;
    this.peerId = fromId;
    this.role = "callee";
    this.hangupSent = false;
    this.quietClose = false;
    this.pendingIncoming = {
      callId,
      fromId,
      buffered: [signal],
    };
    const name = this.resolveName(fromId) ?? fromId;
    this.setUi({ phase: "incoming", peerId: fromId, name, callId });
  }

  handleUnavailable(callId: string, _reason?: string): void {
    if (this.callId !== callId || this.role !== "caller") return;
    this.quietClose = true;
    this.destroyPeerInstance();
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    this.pendingIncoming = null;
    this.resetSessionIds();
    this.setUi({ phase: "missed", message: "Абонент недоступен (офлайн)" });
  }

  handleEnded(fromId: string, callId: string): void {
    if (callId !== this.callId || fromId !== this.peerId) return;
    this.quietClose = true;
    this.destroyPeerInstance();
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    this.pendingIncoming = null;
    this.resetSessionIds();
    this.setUi({ phase: "idle" });
  }
}
